import { msg } from '@lingui/core/macro';
import type {
    CompletionPeriod,
    CompletionPeriodValues,
    StatisticsType,
} from '@routes/sidebar/___shared___/api/statistics.api.ts';
import {
    EMPTY_MONTHLY_PERIOD_VALUE,
    LEARNING_STATISTICS_MODE,
} from '@routes/sidebar/___shared___/components/learning-statistics/constants.ts';
import { STATISTIC_DATERANGES, STATISTICS_TYPES } from '@routes/sidebar/___shared___/constants.ts';
import { translateItem } from '@utils/translations.ts';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

import {
    calculateLastNMonths,
    formatHours,
    getCurrentStatTextAndChart,
    minutesToHours,
    NUMBER_OF_MONTHS,
    TAB_TYPES,
} from '../../../learning/ui/dashboard-tab/___shared___/utils.ts';
import type {
    CommonStatisticsSummary,
    LearningStatisticsOptions,
    LearningStatisticsViewModelProps,
    MonthlyPeriodValue,
} from './types.ts';

export const SHARED_LEARNING_STATISTICS_ELEMENTS_COMPLETED = msg`elements completed`;
export const SHARED_LEARNING_STATISTICS_PATHWAYS_COMPLETED = msg`pathways completed`;

dayjs.extend(isoWeek);

interface CompletedValueAndTextProps {
    type: string;
    totalCompleted: number;
    completedTextTranslated: string;
}

export const getStatisticsSummaryByMode = (options: LearningStatisticsOptions): CommonStatisticsSummary => {
    switch (options.mode) {
        case LEARNING_STATISTICS_MODE.LEARNER:
            return options.learnerStatisticsSummary;

        case LEARNING_STATISTICS_MODE.FLEET:
            return options.fleetStatistics.statisticsSummary;

        case LEARNING_STATISTICS_MODE.VESSEL:
            return options.vesselStatistics.statisticsSummary;

        default:
            return {
                statistics: [],
            };
    }
};

const getEmptyMonthlyCompleted = () => Array.from({ length: NUMBER_OF_MONTHS }, () => 0);

const getMonthlyPeriodValue = (item: CompletionPeriodValues): MonthlyPeriodValue => ({
    ashore: item.ashoreCompleted,
    onboard: item.onboardCompleted,
    total: item.totalCompleted,
});

const getMonthlyPeriodValues = (completionPeriods: CompletionPeriod[], referenceDate: Date): MonthlyPeriodValue[] => {
    const monthly = completionPeriods.find((cp) => cp.statisticDateRange === STATISTIC_DATERANGES.Month);

    if (!monthly) {
        return Array.from({ length: NUMBER_OF_MONTHS }, () => EMPTY_MONTHLY_PERIOD_VALUE);
    }

    const keyCreator = (date: Date) => dayjs(date).format('YYYY-MM');

    const valueMap = new Map(
        monthly.values.map((item) => {
            const date = new Date(item.dateTimeUTCPeriod);
            const key = keyCreator(date);

            return [key, getMonthlyPeriodValue(item)] as const;
        }),
    );

    const result: MonthlyPeriodValue[] = [];

    for (let i = 0; i < NUMBER_OF_MONTHS; i++) {
        const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - i, 1);
        const key = keyCreator(date);

        result.push(valueMap.get(key) ?? EMPTY_MONTHLY_PERIOD_VALUE);
    }

    return result.reverse();
};

const getThisWeekCompleted = (completionPeriods: CompletionPeriod[], referenceDate: Date): number => {
    const weekly = completionPeriods.find((cp) => cp.statisticDateRange === STATISTIC_DATERANGES.Week);

    if (!weekly?.values.length) {
        return 0;
    }

    const weekKey = (date: string | Date) => dayjs(date).startOf('isoWeek').format('YYYY-MM-DD');
    const thisWeek = weekKey(referenceDate);

    const currentWeekValue = weekly.values.find((item) => weekKey(item.dateTimeUTCPeriod) === thisWeek);
    return currentWeekValue?.totalCompleted ?? 0;
};

const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
        return translateItem(msg`${hours} h ${remainingMinutes} min`);
    }

    if (hours > 0) {
        return formatHours(hours);
    }

    return translateItem(msg`${remainingMinutes} min`);
};

const getCompletedValueAndText = ({ type, totalCompleted, completedTextTranslated }: CompletedValueAndTextProps) => {
    switch (type) {
        case STATISTICS_TYPES.Minutes:
            return {
                completedVsTotalText: formatMinutes(totalCompleted),
                text: completedTextTranslated,
            };

        case STATISTICS_TYPES.Elements:
            return {
                completedVsTotalText: String(totalCompleted),
                text: translateItem(SHARED_LEARNING_STATISTICS_ELEMENTS_COMPLETED),
            };

        case STATISTICS_TYPES.Pathways:
            return {
                completedVsTotalText: String(totalCompleted),
                text: translateItem(SHARED_LEARNING_STATISTICS_PATHWAYS_COMPLETED),
            };

        default:
            return {
                completedVsTotalText: String(totalCompleted),
                text: completedTextTranslated,
            };
    }
};

const getChartValue = (value: number, isMinutesTab: boolean) => {
    return isMinutesTab ? minutesToHours(value) : value;
};

export const getLearningStatisticsViewModel = ({
    mode,
    statisticsSummary,
    serverTimeDate,
    selectedTabSection,
    completedTextTranslated,
}: LearningStatisticsViewModelProps) => {
    const currentDate = new Date(serverTimeDate);
    const last6Months = calculateLastNMonths(currentDate.getMonth(), NUMBER_OF_MONTHS);
    const emptyMonthlyCompleted = getEmptyMonthlyCompleted();

    const emptyViewModel = {
        last6Months,
        ashoreData: emptyMonthlyCompleted,
        onboardData: emptyMonthlyCompleted,
        completedData: emptyMonthlyCompleted,
        weekCompletedText: 0,
        monthCompletedText: 0,
        completedVsTotalText: '',
        text: '',
    };

    if (!statisticsSummary?.statistics?.length) {
        return emptyViewModel;
    }

    const statisticsByType = statisticsSummary.statistics.map((s) => ({
        type: s.statisticsType as StatisticsType,
        monthly: getMonthlyPeriodValues(s.completionPeriods, currentDate),
        total: s.total,
        totalCompleted: s.totalCompleted,
        weekCompleted: getThisWeekCompleted(s.completionPeriods, currentDate),
    }));

    const chartStatistics = statisticsByType.map(({ type, total, totalCompleted, monthly, weekCompleted }) => ({
        type,
        total,
        totalCompleted,
        monthlyCompleted: monthly.map((item) => item.total),
        weekCompleted,
    }));

    const selectedType = TAB_TYPES[selectedTabSection];
    const selectedStatistic = statisticsByType.find((item) => item.type === selectedType);

    let currentStatTextAndChart;

    try {
        currentStatTextAndChart = getCurrentStatTextAndChart(
            selectedTabSection,
            chartStatistics,
            completedTextTranslated,
        );
    } catch {
        return emptyViewModel;
    }

    if (!currentStatTextAndChart?.monthlyCompleted || !selectedStatistic) {
        return emptyViewModel;
    }

    const isLearnerMode = mode === LEARNING_STATISTICS_MODE.LEARNER;
    const isMinutesTab = selectedType === STATISTICS_TYPES.Minutes;
    const { monthly } = selectedStatistic;

    const ashoreData = isLearnerMode
        ? monthly.map((item) => getChartValue(item.ashore, isMinutesTab))
        : emptyMonthlyCompleted;

    const onboardData = isLearnerMode
        ? monthly.map((item) => getChartValue(item.onboard, isMinutesTab))
        : emptyMonthlyCompleted;

    const completedData = currentStatTextAndChart.monthlyCompleted;

    const completedValueAndText = getCompletedValueAndText({
        type: selectedStatistic.type,
        totalCompleted: selectedStatistic.totalCompleted,
        completedTextTranslated,
    });

    return {
        last6Months,
        ashoreData,
        onboardData,
        completedData,
        weekCompletedText: currentStatTextAndChart.weekCompletedText ?? 0,
        monthCompletedText: currentStatTextAndChart.monthCompletedText ?? 0,
        completedVsTotalText: completedValueAndText.completedVsTotalText,
        text: completedValueAndText.text,
    };
};
